import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import { Content, Header, Page } from '@backstage/core-components';
import { TeamEditForm } from './TeamEditForm';
import { useCanEditTeam } from '../../hooks/useCanEditTeam';

export function TeamEditPage() {
  const {
    namespace = 'default',
    kind = 'group',
    name = '',
  } = useParams<{
    namespace?: string;
    kind?: string;
    name?: string;
  }>();

  const catalogApi = useApi(catalogApiRef);
  const isEditableRoute = kind === 'group' && namespace === 'default';
  const canEdit = useCanEditTeam(isEditableRoute ? name : '');
  const [loaded, setLoaded] = useState<{
    ref: string;
    entity?: GroupEntity;
    error?: string;
  } | null>(null);

  const entityRef = `${kind}:${namespace}/${name}`;

  useEffect(() => {
    if (!isEditableRoute) return undefined;
    let cancelled = false;
    catalogApi
      .getEntityByRef(entityRef)
      .then(e => {
        if (cancelled) return;
        setLoaded(
          e
            ? { ref: entityRef, entity: e as GroupEntity }
            : { ref: entityRef, error: `Team not found: ${entityRef}` },
        );
      })
      .catch(err => {
        if (!cancelled) setLoaded({ ref: entityRef, error: String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, entityRef, isEditableRoute]);

  const current = loaded?.ref === entityRef ? loaded : undefined;
  const entity = current?.entity;
  const error = current?.error;
  const loading = isEditableRoute && !current;

  const title = entity?.metadata.title ?? entity?.metadata.name ?? name;

  return (
    <Page themeId="home">
      <Header
        title={loading ? 'Edit Team' : title}
        subtitle={loading ? '' : 'Edit team details'}
        type="Team"
        typeLink={
          entity ? `/catalog/default/group/${entity.metadata.name}` : undefined
        }
      />
      <Content>
        {(!isEditableRoute || canEdit === false) && (
          <Typography color="error">
            You can't edit this team. Editing may be disabled, or limited to the
            team's lead, PM, tech lead or architect (or those of a parent
            group).
          </Typography>
        )}
        {isEditableRoute &&
          (loading || canEdit === null) &&
          canEdit !== false && (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          )}
        {error && isEditableRoute && canEdit !== false && (
          <Typography color="error">{error}</Typography>
        )}
        {entity && isEditableRoute && canEdit && (
          <TeamEditForm entity={entity} />
        )}
      </Content>
    </Page>
  );
}
